import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CalendarX2, Loader2, MapPin, Ticket } from "lucide-react";
import Logo from "@/components/Logo";
import { formatApiErrorDetail } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_MSG =
  "Ahora mismo no hay visitas disponibles. Publicamos los pisos a las 9:00 y, con la alta demanda, los huecos disponibles duran solo unas horas.";

const inputCls =
  "w-full rounded-2xl border border-[#12263a]/15 bg-white px-4 py-3 text-sm text-[#12263a] outline-none transition-colors duration-200 placeholder:text-[#12263a]/30 focus:border-[#0f766e] dark:border-white/15 dark:bg-white/5 dark:text-[#f7f4ee] dark:placeholder:text-[#f7f4ee]/30 dark:focus:border-[#14b8a6]";

const labelCls = "mb-1.5 block text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#12263a]/50 dark:text-[#f7f4ee]/45";

export default function Visita() {
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [checking, setChecking] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [form, setForm] = useState({ nombre: "", apellidos: "", email: "", zona: "", dia: "", hora: "" });
  const [sent, setSent] = useState(false);
  const [noVisitsMsg, setNoVisitsMsg] = useState("");
  const [sending, setSending] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setCodeError("");
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/visits/lookup`, { code });
      setReservation(data);
    } catch (err) {
      setCodeError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/visits/request`, form);
      setNoVisitsMsg(data.message || FALLBACK_MSG);
    } catch (err) {
      setNoVisitsMsg(FALLBACK_MSG);
    } finally {
      setSending(false);
      setSent(true);
    }
  };

  const reset = () => {
    setReservation(null);
    setSent(false);
    setCode("");
    setCodeError("");
  };

  return (
    <div className="min-h-screen bg-[#f7f4ee] dark:bg-[#0c1a29]" data-testid="visita-page">
      <header className="bg-[#12263a]">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label="Nidoria — inicio" data-testid="visita-logo-link">
            <Logo variant="light" />
          </Link>
          <button
            onClick={reset}
            data-testid="visita-back-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 transition-colors duration-200 hover:text-[#14b8a6]"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 py-10 sm:py-14">
        <AnimatePresence mode="wait">
          {reservation ? (
            <motion.div
              key="reserva"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="overflow-hidden rounded-3xl border border-[#12263a]/10 bg-white shadow-sm dark:border-white/10 dark:bg-[#102336]"
              data-testid="reserva-resultado"
            >
              {reservation.image_url && (
                <img
                  src={reservation.image_url}
                  alt={reservation.property_title}
                  className="h-48 w-full object-cover"
                  data-testid="reserva-foto"
                />
              )}
              <div className="p-6 sm:p-7">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#0f766e] dark:text-[#14b8a6]">Su reserva</p>
                <p className="mt-2 inline-block rounded-xl bg-[#0f766e]/10 px-3 py-1.5 font-mono text-lg font-bold text-[#0f766e] dark:text-[#14b8a6]" data-testid="reserva-codigo">
                  {reservation.code}
                </p>
                <h1 className="mt-4 font-serif text-2xl font-semibold text-[#12263a] dark:text-[#f7f4ee]" data-testid="reserva-inmueble">
                  {reservation.property_title}
                </h1>
                <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-[#12263a]/60 dark:text-[#f7f4ee]/55" data-testid="reserva-ubicacion">
                  <MapPin className="h-4 w-4 text-[#f59e0b]" />
                  {reservation.location}
                </p>
                <p className="mt-4 text-xs leading-relaxed text-[#12263a]/45 dark:text-[#f7f4ee]/40">
                  Presente este código el día de la visita. Si tiene cualquier duda, escríbanos por WhatsApp.
                </p>
                <button
                  onClick={reset}
                  data-testid="reserva-volver-btn"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#0f766e] transition-colors duration-200 hover:text-[#12263a] dark:text-[#14b8a6] dark:hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Volver
                </button>
              </div>
            </motion.div>
          ) : sent ? (
            <motion.div
              key="sin-huecos"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="rounded-3xl border border-[#12263a]/10 bg-white p-7 shadow-sm dark:border-white/10 dark:bg-[#102336] sm:p-8"
              data-testid="visita-no-disponible"
            >
              <span className="inline-flex rounded-2xl bg-[#f59e0b]/10 p-3 text-[#f59e0b]">
                <CalendarX2 className="h-6 w-6" />
              </span>
              <h1 className="mt-5 font-serif text-2xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">
                No hay visitas disponibles
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-[#12263a]/65 dark:text-[#f7f4ee]/60">{noVisitsMsg}</p>
              <p className="mt-3 rounded-2xl bg-[#0f766e]/5 px-4 py-3 text-xs font-semibold leading-relaxed text-[#0f766e] dark:bg-[#14b8a6]/10 dark:text-[#14b8a6]">
                Consejo: mira la app a primera hora, sobre las 9:00. Es cuando salen los nuevos pisos y los huecos de visita.
              </p>
              <button
                onClick={reset}
                data-testid="visita-volver-btn"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#0f766e] transition-colors duration-200 hover:text-[#12263a] dark:text-[#14b8a6] dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="formulario"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="space-y-6"
            >
              <div className="rounded-3xl border border-[#12263a]/10 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#102336] sm:p-7">
                <span className="inline-flex rounded-2xl bg-[#12263a]/5 p-3 text-[#12263a] dark:bg-white/10 dark:text-[#f7f4ee]">
                  <Ticket className="h-5 w-5" />
                </span>
                <h1 className="mt-4 font-serif text-xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">
                  ¿Tiene ya una reserva?
                </h1>
                <p className="mt-1.5 text-sm text-[#12263a]/55 dark:text-[#f7f4ee]/50">
                  Escriba su número de reserva para ver los detalles de su visita.
                </p>
                <form onSubmit={lookup} className="mt-5 flex gap-2.5">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    placeholder="NDR-1234"
                    data-testid="reserva-codigo-input"
                    className={`${inputCls} font-mono uppercase`}
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    disabled={checking}
                    data-testid="reserva-ok-btn"
                    className="shrink-0 rounded-2xl bg-[#12263a] px-6 text-sm font-extrabold text-white transition-transform duration-200 hover:scale-[1.04] active:scale-95 disabled:opacity-60"
                  >
                    {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "OK"}
                  </button>
                </form>
                {codeError && (
                  <p className="mt-3 rounded-2xl bg-red-500/10 px-4 py-2.5 text-xs font-bold text-red-500" data-testid="reserva-error">
                    {codeError}
                  </p>
                )}
              </div>

              <div className="rounded-3xl border border-[#12263a]/10 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#102336] sm:p-7">
                <span className="inline-flex rounded-2xl bg-[#0f766e]/10 p-3 text-[#0f766e] dark:text-[#14b8a6]">
                  <MapPin className="h-5 w-5" />
                </span>
                <h2 className="mt-4 font-serif text-xl font-semibold text-[#12263a] dark:text-[#f7f4ee]">Reserva tu visita</h2>
                <p className="mt-1.5 text-sm text-[#12263a]/55 dark:text-[#f7f4ee]/50">
                  Dinos quién eres y qué zona buscas, y comprobamos la disponibilidad al momento.
                </p>
                <form onSubmit={submitRequest} className="mt-5 space-y-4">
                  <div>
                    <label className={labelCls} htmlFor="visita-nombre">Nombre</label>
                    <input
                      id="visita-nombre"
                      required
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      placeholder="María"
                      data-testid="visita-nombre-input"
                      className={inputCls}
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="visita-apellidos">Apellidos</label>
                    <input
                      id="visita-apellidos"
                      required
                      value={form.apellidos}
                      onChange={(e) => setForm({ ...form, apellidos: e.target.value })}
                      placeholder="García López"
                      data-testid="visita-apellidos-input"
                      className={inputCls}
                      autoComplete="family-name"
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="visita-email">Correo</label>
                    <input
                      id="visita-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="tu@correo.com"
                      data-testid="visita-email-input"
                      className={inputCls}
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="visita-zona">¿Qué zona buscas?</label>
                    <input
                      id="visita-zona"
                      required
                      value={form.zona}
                      onChange={(e) => setForm({ ...form, zona: e.target.value })}
                      placeholder="Ej.: Ruzafa, Valencia"
                      data-testid="visita-zona-input"
                      className={inputCls}
                    />
                    <p className="mt-1.5 text-xs text-[#12263a]/40 dark:text-[#f7f4ee]/35">
                      Dinos dónde te interesa comprar o alquilar.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls} htmlFor="visita-dia">Día</label>
                      <input
                        id="visita-dia"
                        type="date"
                        required
                        value={form.dia}
                        onChange={(e) => setForm({ ...form, dia: e.target.value })}
                        data-testid="visita-dia-input"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="visita-hora">Hora</label>
                      <input
                        id="visita-hora"
                        type="time"
                        required
                        value={form.hora}
                        onChange={(e) => setForm({ ...form, hora: e.target.value })}
                        data-testid="visita-hora-input"
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="visita-submit-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0f766e] py-4 text-sm font-extrabold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                  >
                    {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                    OK
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
